import React, {useEffect, useMemo, useRef, useState} from 'react';
import SingleSelect from 'components/SingleSelect';
import SearchSelect from 'components/SearchSelect';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import EditableDrawer from 'components/EditableDrawer';
import { useTmsSearch } from '../../hooks/useTmsSearch';
import { CREATE_TMS_TEMPORARY_TASK, CreateTmsTaskInput, CreateTmsTaskResponse, TmsTaskTemporaryDriverInput, TmsTaskTemporaryVehicleInput, TmsTaskTransitInfoInput } from '../graphql/mutations/taskMutations';
import { message, Modal, AutoComplete, Form, Radio } from 'antd';
import InputFilterItem from 'components/InputFilterItem';
import DatePickerFilter from 'components/DatePickerFilter';
import { TmsBusinessTypeEnum, TmsTaskStatusEnum, TmsTaskTypeEnum, TmsTaskSourceEnum, TmsStatusEnum, WaypointTypeEnum, TmsRouteTypeEnum, HandoverTypeEnum, ALL_CENTER_WAREHOUSE_ID } from 'enums/TmsEnum';
import dayjs, { Dayjs } from 'dayjs';
import { t } from '../../../../i18n';
import { showError } from '../../../../utils/exceptionUtils';
import { getDepartureDateRange, getTaskDateRange, isDayOutOfRange, toDay } from '../utils/taskDateRange';
import { resultErrorMessage } from '../../../../utils/resultCode';
import { useFilterLayoutStyles } from '../../../../utils/languageUtils';
import {useTmsVehicleAndModel} from "../../hooks/useTmsVehicleAndModel";
import { useTmsDriversByCarrier } from '../../hooks/useTmsDriversByCarrier';
import DriverPhoneRow, { isPhoneInvalid, checkDriverPhoneDuplicate } from './DriverPhoneRow';
import { useTmsVehiclesByRoute } from '../../hooks/useTmsVehiclesByRoute';
import { useTmsVehiclesByCarrier } from '../../hooks/useTmsVehiclesByCarrier';
import { GET_TMS_CARRIERS_BY_WAREHOUSE_ID, GetTmsCarriersByWarehouseIdResponse, TmsCarrierType } from '../../route/graphql/queries/getTmsCarriers';
import { GET_HANDOVER_CARRIERS_BY_WAREHOUSE_ID, GetHandoverCarriersByWarehouseIdResponse } from '../../route/graphql/queries/getHandoverCarriers';
import { GET_TMS_ROUTE_WAYPOINTS_WITH_SORTING_CODE_BY_ROUTE_ID, GetTmsRouteWaypointsWithSortingCodeByRouteIdResponse } from '../../route/graphql/queries/getTmsRoutes';
import { GET_HUB_LIST, GetHubListResponse } from '../../../wms/bags/graphql/queries/getHubList';
import TransitInfoTable, { TransitInfoRow } from './TransitInfoTable';
import { IS_TRANSIT } from './TransitSection';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';
import { FuelTypeEnum, getFuelTypeLabel } from '../../../../enums/FuelTypeEnum';
import TmsTaskConfirmDriverVehicleModal from './TmsTaskConfirmDriverVehicleModal';
import { CHECK_ACCOUNT_BY_PHONE, CheckAccountByPhoneResponse } from '../graphql/queries/checkAccountByPhone';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { useFilteredCarriers, isCarrierValidForRouteType } from '../../hooks/useFilteredCarriers';
import { GET_VEHICLES, VehiclesResponse } from '../../vehicle/graphql/queries/getVehicles';
import { FIND_VEHICLE_TYPE_BY_ID, FindVehicleTypeByIdResponse } from '../../../../graphql/queries/getAllVehicleTypes';
import { parseDecimalStringToCents, sanitizeDecimalInput } from '../utils/perVehicleCost';
import { escortCountOptions } from '../utils/escortCount';

interface FilterOptions {
    routeId: string;
    businessType: string;
    taskDate: string;
    planDepartureDate: string;
    originPlannedDepartureTime: string;
    taskType: TmsTaskTypeEnum;
    // 司机1：必填（交接类型除外）
    driver1Id: string;
    driver1Name: string; // 司机名称（用于自由输入）
    driver1PhoneNumber: string;
    // 司机2：非必填，仅BOL展示，不参与派单/APP/结算
    driver2Id: string;
    driver2Name: string;
    driver2PhoneNumber: string;
    vehicleId: string;
    licensePlate: string; // 车辆名称（用于自由输入）
    vehicleModel: string;
    vehicleTypeId: number | null;
    status: TmsTaskStatusEnum;
    trailerNumber: string;
    escortCount: string;
    perVehicleCost: string;
    gpsTracking: string;
    remark: string;
}

interface CreateTemporaryTaskDrawerProps {
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
    submitting?: boolean;
}

const initialFilters: FilterOptions = {
    routeId: '',
    businessType: '',
    taskDate: '',
    planDepartureDate: '',
    originPlannedDepartureTime: '',
    taskType: TmsTaskTypeEnum.Temporary,
    driver1Id: '',
    driver1Name: '',
    driver1PhoneNumber: '',
    driver2Id: '',
    driver2Name: '',
    driver2PhoneNumber: '',
    vehicleId: '',
    licensePlate: '',
    vehicleModel: '',
    vehicleTypeId: null,
    status: TmsTaskStatusEnum.Draft,
    trailerNumber: '',
    escortCount: '1',
    perVehicleCost: '',
    gpsTracking: '',
    remark: '',
};

const CreateTemporaryTaskDrawer: React.FC<CreateTemporaryTaskDrawerProps> = ({
    open,
    onClose,
    onSubmit,
    submitting = false,
}) => {
    const drawerTitle = String(t('tms.task.drawer.createTemporaryTaskTitle'));
    const { handleRouteSearch } = useTmsSearch();

    // 获取与 SingleSelect 一致的布局样式
    const { containerClassName, labelClassName, inputContainerClassName } = useFilterLayoutStyles('w-[7em]', undefined, true);

    const [filters, setFilters] = useState<FilterOptions>(initialFilters);
    // 用于强制 SearchSelect 重新渲染的 key
    const [routeSelectKey, setRouteSelectKey] = useState(0);
    const [departureTime, setDepartureTime] = useState<Dayjs | null>(null);
    // 标记 vehicle 是否是从司机查询自动设置的
    const [isVehicleAutoSet, setIsVehicleAutoSet] = useState(false);
    // 标记 vehicle 是否是自由输入的（不在列表中）
    const [isVehicleFreeInput, setIsVehicleFreeInput] = useState(false);
    // 标记 phoneNumber 是否来自司机信息自动填充
    const [isDriver1PhoneAutoFilled, setIsDriver1PhoneAutoFilled] = useState(false);
    // 司机2 同上：只在「选中系统司机并带出了电话」时置灰，与司机1 同一套口径
    const [isDriver2PhoneAutoFilled, setIsDriver2PhoneAutoFilled] = useState(false);
    // 标记 driver 是否是自由输入的（不在列表中）
    const [isDriver1FreeInput, setIsDriver1FreeInput] = useState(false);
    /**
     * 用户手打过司机2 姓名（由 DriverPhoneRow 的 onDriverCleared 置位），与司机1 同一套口径。
     * 不能只看 filters 派生：手输的临时司机不会写回 driver2Id，光凭「有姓名无 ID」区分不出
     * 「本次手输」和「回显的历史手输」，会导致重复建号并被重号检测误拦（编辑抽屉同）。
     */
    const [driver2TypedByUser, setDriver2TypedByUser] = useState(false);
    // 确认弹窗的状态
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    // 确认弹窗中的表单实例
    const [driver1Form] = Form.useForm();
    const [driver2Form] = Form.useForm();
    const [vehicleForm] = Form.useForm();
    // 确认弹窗提交中状态
    const [confirmSubmitting, setConfirmSubmitting] = useState(false);
    const { currentWarehouse } = useWarehouseContext();
    // 选中的承运商ID（下拉框值）；选择路线后自动带出路线承运商，用户可改选
    const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
    const prevCarrierIdRef = useRef<string>('');
    const businessTypeSyncedRouteIdRef = useRef<string | null>(null);

    const taskTypeOptions = useMemo(() => [
        { label: String(t('tms.task.taskTypes.regular')), value: TmsTaskTypeEnum.Regular },
        { label: String(t('tms.task.taskTypes.temporary')), value: TmsTaskTypeEnum.Temporary },
    ], [t]);

    const businessTypeOptions = useMemo(() => [
        { label: String(t('tms.task.businessTypes.ownRoute')), value: String(TmsBusinessTypeEnum.OwnRoute) },
        { label: String(t('tms.task.businessTypes.temuGA')), value: String(TmsBusinessTypeEnum.TemuGA) },
        { label: String(t('tms.task.businessTypes.temuSCF')), value: String(TmsBusinessTypeEnum.TemuSCF) },
        { label: String(t('tms.task.businessTypes.tiktok')), value: String(TmsBusinessTypeEnum.TikTok) },
    ], [t]);

    // 当 drawer 打开时，重置所有状态到初始值
    useEffect(() => {
        if (open) {
            setFilters({ ...initialFilters });
            setSelectedCarrierId('');
            setDepartureTime(null);
            setIsVehicleAutoSet(false);
                        setIsVehicleFreeInput(false);
            setIsDriver1PhoneAutoFilled(false);
            setIsDriver2PhoneAutoFilled(false);
            setIsDriver1FreeInput(false);
            setDriver2TypedByUser(false);
            setConfirmModalVisible(false);
            setIsBulkConsolidated(false);
            setIsTransit(IS_TRANSIT.NO);
            setTransitRows([]);
            businessTypeSyncedRouteIdRef.current = null;
            // 更新 key 以强制 SearchSelect 重新渲染并清空选项
            setRouteSelectKey(prev => prev + 1);
        }
    }, [open]);

    const handleClose = () => {
        setFilters({ ...initialFilters });
        setDepartureTime(null);
        onClose();
    };

    // 创建任务的 mutation
    const [createTemporaryTask, { loading: creating }] = useMutation<CreateTmsTaskResponse>(CREATE_TMS_TEMPORARY_TASK);

    // 按车牌号查询车辆（自由编辑车辆时弹窗前校验是否已绑定其他承运商）
    const [getVehiclesByLicensePlate] = useLazyQuery<VehiclesResponse>(GET_VEHICLES);
    const [findVehicleTypeById] = useLazyQuery<FindVehicleTypeByIdResponse>(FIND_VEHICLE_TYPE_BY_ID);

    // 使用Hook根据routeId获取 selectedRoute、carrierIdForQuery（路线默认承运商）
    const { selectedRoute } = useTmsVehiclesByRoute(filters.routeId);

    // 是否为交接类型路线
    const isHandoverRoute = Number(selectedRoute?.routeType) === TmsRouteTypeEnum.Handover;

    // 转运信息：仅「干线 + 自有路线」任务展示，与编辑任务页 showTransit 判定口径一致
    const showTransit = Number(selectedRoute?.routeType) === TmsRouteTypeEnum.Linehaul
        && Number(selectedRoute?.businessType) === TmsBusinessTypeEnum.OwnRoute;
    // 是否散货拼车
    const [isBulkConsolidated, setIsBulkConsolidated] = useState<boolean>(false);
    // 是否存在转运：'0'-否 '1'-是
    const [isTransit, setIsTransit] = useState<string>(IS_TRANSIT.NO);
    // 转运行数据（由 TransitInfoTable 维护，提交时一并传给 CreateTmsTaskInput）
    const [transitRows, setTransitRows] = useState<TransitInfoRow[]>([]);

    // 转运下拉候选（干线）：以 HUB list 为全集
    //  - 转运目的地站：HUB list，本路线站点(始发/途径/终点)剔除
    //  - 转运站：本路线的途径站 + 终到站（创建阶段任务尚未发生，不存在"已发车"状态，无需置灰）
    const { data: hubListData } = useQuery<GetHubListResponse>(GET_HUB_LIST, { skip: !open || !showTransit });
    const { data: routeWaypointsData } = useQuery<GetTmsRouteWaypointsWithSortingCodeByRouteIdResponse>(
        GET_TMS_ROUTE_WAYPOINTS_WITH_SORTING_CODE_BY_ROUTE_ID,
        {
            variables: { routeId: filters.routeId ? Number(filters.routeId) : 0 },
            skip: !open || !showTransit || !filters.routeId,
        }
    );
    const { transitStationOptions, transitDestStationOptions } = useMemo(() => {
        const hubs = hubListData?.hubList ?? [];
        const routeWaypoints = routeWaypointsData?.getTmsRouteWaypointsWithSortingCodeByRouteId?.waypoints ?? [];
        const routeStationIds = new Set(routeWaypoints.map((w) => String(w.waypointId)));
        // 转运目的地站：HUB list 剔除本路线站点(始发/途径/终点)
        const destStationOptions = hubs
            .filter((hub) => !routeStationIds.has(String(hub.id)))
            .map((hub) => ({ value: hub.id, label: hub.sortingCode! }));
        // 转运站：本路线的途径站 + 终到站
        const stationOptions = routeWaypoints
            .filter((waypoint) => waypoint.waypointType === WaypointTypeEnum.Waypoint || waypoint.waypointType === WaypointTypeEnum.Destination)
            .map((waypoint) => ({
                value: String(waypoint.waypointId),
                label: waypoint.waypointSortingCode!,
            }));
        return { transitStationOptions: stationOptions, transitDestStationOptions: destStationOptions };
    }, [hubListData, routeWaypointsData]);

    // 选"是"且尚无行时自动补一行；切"否"由下面 handleIsTransitChange 清空表格，下次切"是"重新触发本 effect
    useEffect(() => {
        if (isTransit === IS_TRANSIT.YES && transitRows.length === 0) {
            setTransitRows([{ key: `transit-${Date.now()}`, transitWarehouseId: '', transitDestWarehouseId: '' }]);
        }
    }, [isTransit, transitRows.length]);

    // 切换是/否时清空表格（再次选"是"由上面 effect 重新初始化一行，相当于重置）
    const handleIsTransitChange = (checked: boolean) => {
        setTransitRows([]);
        setIsTransit(checked ? IS_TRANSIT.YES : IS_TRANSIT.NO);
    };

    // 跟车人数为1时仅司机1；>1才需要司机2
    const showDriver2 = Number(filters.escortCount) > 1;

    /**
     * 司机2 是手输（本次手打了姓名且没选中系统司机）→ 同样要建司机账号，走确认弹窗。
     * 交接类型排除：司机是对方承运商的人，系统里没账号，与司机1 口径一致。
     */
    const isDriver2FreeInput = showDriver2 && !isHandoverRoute && driver2TypedByUser && !!filters.driver2Name && !filters.driver2Id;

    // 判断是否需要单车成本：交接类型不需要，其他都需要
    const shouldIncludePerVehicleCost = !isHandoverRoute;

    // 选中路线后，同步 businessType / 承运商类型 / 承运商（businessType 每条路线只同步一次）
    useEffect(() => {
        if (!filters.routeId) {
            businessTypeSyncedRouteIdRef.current = null;
            return;
        }
        if (!selectedRoute || String(selectedRoute.id) !== String(filters.routeId)) {
            return;
        }

        // businessType：每条路线只同步一次
        if (businessTypeSyncedRouteIdRef.current !== String(filters.routeId)) {
            const businessTypeFromRoute = selectedRoute.businessType;
            if (businessTypeFromRoute != null) {
                setFilters(prev => {
                    const next = String(businessTypeFromRoute);
                    return prev.businessType === next ? prev : { ...prev, businessType: next };
                });
            }
            businessTypeSyncedRouteIdRef.current = String(filters.routeId);
        }

        // 承运商
        if (selectedRoute.carrierId != null) {
            const nextCarrierId = String(selectedRoute.carrierId);
            setSelectedCarrierId(prev => (prev === nextCarrierId ? prev : nextCarrierId));
        }

        // carrierSourceType 已在交接方需求中废弃，不再同步
    }, [filters.routeId, selectedRoute]);

    // 承运商切换后，清空后续依赖字段（司机/联系方式/车辆/车型等），避免残留上一次承运商的数据
    useEffect(() => {
        if (prevCarrierIdRef.current === selectedCarrierId) return;
        prevCarrierIdRef.current = selectedCarrierId;

        setFilters(prev => ({
            ...prev,
            driver1Id: '',
            driver1Name: '',
            driver1PhoneNumber: '',
            driver2Id: '',
            driver2Name: '',
            driver2PhoneNumber: '',
            vehicleId: '',
            licensePlate: '',
            vehicleModel: '',
            vehicleTypeId: null,
        }));

        setIsVehicleAutoSet(false);
        setIsVehicleFreeInput(false);
        setIsDriver1PhoneAutoFilled(false);
        setIsDriver2PhoneAutoFilled(false);
        setIsDriver1FreeInput(false);
        setDriver2TypedByUser(false);
    }, [selectedCarrierId]);

    // 根据选中的承运商获取司机列表（交接路线无需查询）
    const { driverOptions, driversData } = useTmsDriversByCarrier(isHandoverRoute ? null : selectedCarrierId || null);
    // 司机ID → 电话，供 DriverPhoneRow 选中系统司机后自动带出电话
    const driverPhoneById = useMemo(
        () => Object.fromEntries(
            (driversData?.getAccountTmsDriversByCarrierId ?? []).map(d => [d.id.toString(), d.phoneNumber])
        ),
        [driversData]
    );
    // 同一个人不能既是司机1 又是司机2：各自下拉里去掉对方已选中的司机（两个方向都挡，不管先选哪个）
    const driver1Options = useMemo(
        () => driverOptions.filter(o => o.value !== filters.driver2Id),
        [driverOptions, filters.driver2Id]
    );
    const driver2Options = useMemo(
        () => driverOptions.filter(o => o.value !== filters.driver1Id),
        [driverOptions, filters.driver1Id]
    );

    // 根据选中的承运商获取车辆列表（交接路线无需查询）
    const { vehiclesData, vehicleOptions, vehiclesLoading: driverVehicleLoading } = useTmsVehiclesByCarrier(isHandoverRoute ? null : selectedCarrierId || null);

    // 获取所有车型（进入页面时加载）
    const { vehicleTypes } = useVehicleTypes();

    // 确认弹窗司机表单的车辆下拉：复用上面同一承运商的车辆数据，不再按 warehouse 维度另发一次查询
    const driverVehicleOptions = useMemo(() =>
        (vehiclesData?.getTmsVehiclesWithModel ?? []).map(vehicle => ({
            value: vehicle.id,
            label: vehicle.licensePlate || ''
        })), [vehiclesData]);

    // 按手机号检查账号是否存在（临时任务创建时校验联系方式）
    const [checkAccountByPhoneQuery] = useLazyQuery<CheckAccountByPhoneResponse>(CHECK_ACCOUNT_BY_PHONE);

    // 司机1/2 电话的异步重号检测结果，传给 DriverPhoneRow 展示
    const [driver1PhoneError, setDriver1PhoneError] = useState<string | null>(null);
    const [driver2PhoneError, setDriver2PhoneError] = useState<string | null>(null);

    const runCheckAccountByPhone = async (phoneNumber: string) =>
        (await checkAccountByPhoneQuery({ variables: { phoneNumber } })).data?.checkAccountByPhone;

    /**
     * 是否要对这个号码做重号检测，司机1/2 同一套口径：非交接，且号码是用户自己填的。
     * 新建没有「详情原值」，只需排掉选中系统司机自动带出的号（autoFilled）——那个号本就属于该司机，
     * 查了必然误报；号码为空由检测方法内部放过。与编辑抽屉同口径。
     */
    const needPhoneCheck = (autoFilled: boolean) => !isHandoverRoute && !autoFilled;

    /**
     * 司机1/2 电话失焦时做重号检测。
     * 返回错误文案而不只 setState：提交时读 state，闭包里还是旧值。
     */
    const validateDriver1PhoneAsync = async (phoneNumber: string): Promise<string | null> => {
        setDriver1PhoneError(null);
        const error = await checkDriverPhoneDuplicate({
            phoneNumber,
            enabled: needPhoneCheck(isDriver1PhoneAutoFilled),
            checkAccountByPhone: runCheckAccountByPhone,
            t: (key) => String(t(key)),
        });
        setDriver1PhoneError(error);
        return error;
    };

    const validateDriver2PhoneAsync = async (phoneNumber: string): Promise<string | null> => {
        setDriver2PhoneError(null);
        const error = await checkDriverPhoneDuplicate({
            phoneNumber,
            enabled: needPhoneCheck(isDriver2PhoneAutoFilled),
            conflictPhoneNumber: filters.driver1PhoneNumber,
            checkAccountByPhone: runCheckAccountByPhone,
            t: (key) => String(t(key)),
        });
        setDriver2PhoneError(error);
        return error;
    };

    // 确认弹窗打开后，将主表单的司机信息（含已选车辆）填入弹窗中的司机表单（司机为自由编辑时）
    // 使用选项中的实际 value，保证 Select 能正确匹配并显示 label（如 "车号 - 车牌"）
    useEffect(() => {
        if (!confirmModalVisible) return;
        const matchedOption = filters.vehicleId && driverVehicleOptions.find(
            o => Number(o.value) === Number(filters.vehicleId)
        );
        const vehicleIdValue = matchedOption ? matchedOption.value : (filters.vehicleId ? Number(filters.vehicleId) : undefined);

        // 不传 password：setFieldsValue 只覆盖传入的字段，密码框保留用户已输入的值
        if (isDriver1FreeInput) {
            driver1Form.setFieldsValue({
                name: filters.driver1Name,
                phone: {
                    countryCode: 1,
                    phoneNumber: filters.driver1PhoneNumber || ''
                },
                role: 'TMS_DRIVER',
                status: 1,
                vehicleId: vehicleIdValue
            });
        }
        // 司机2 不绑定车辆，故不回填车辆字段
        if (isDriver2FreeInput) {
            driver2Form.setFieldsValue({
                name: filters.driver2Name,
                phone: {
                    countryCode: 1,
                    phoneNumber: filters.driver2PhoneNumber || ''
                },
                role: 'TMS_DRIVER',
                status: 1
            });
        }
    }, [confirmModalVisible, isDriver1FreeInput, isDriver2FreeInput, filters.vehicleId, filters.driver1Name, filters.driver1PhoneNumber, filters.driver2Name, filters.driver2PhoneNumber, driverVehicleOptions, driver1Form, driver2Form]);

    // 车型选项（确认弹窗用，value 为 number ID）
    const vehicleModelOptions = useMemo(() => {
        return (vehicleTypes || [])
            .map(vt => ({
                label: vt.nameEn,
                value: vt.id
            }));
    }, [vehicleTypes]);


    // 确认弹窗打开后，将主表单的车型填入弹窗中的车辆表单（车辆为自由输入时）
    useEffect(() => {
        if (confirmModalVisible && isVehicleFreeInput) {
            vehicleForm.setFieldsValue({
                licensePlate: filters.licensePlate,
                status: 1,
                // 车型 option.value 是字符串（GraphQL Long 序列化成 String），数字对不上会显示原始 ID
                vehicleTypeId: filters.vehicleTypeId ? String(filters.vehicleTypeId) : undefined
            });
            if (filters.vehicleTypeId) {
                handleVehicleTypeChange(filters.vehicleTypeId);
            }
        }
    }, [confirmModalVisible, isVehicleFreeInput, filters.vehicleTypeId, filters.licensePlate]);

    // 燃料类型选项
    const fuelTypeOptions = useMemo(() => {
        return Object.values(FuelTypeEnum)
            .filter(v => typeof v === 'number')
            .map(v => ({ label: getFuelTypeLabel(v as FuelTypeEnum, t), value: v as number }));
    }, [t]);

    // 状态选项
    const statusOptions = useMemo(() => [
        { value: 1, label: String(t('enum.activeStatus.active')) },
        { value: 0, label: String(t('enum.activeStatus.suspended')) }
    ], [t]);

    // 查询所有承运商，用于下拉框（按当前仓过滤）
    const { data: allCarriersData } = useQuery<GetTmsCarriersByWarehouseIdResponse>(GET_TMS_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : null
        },
        skip: !currentWarehouse?.id
    });

    // 查询交接方（仅交接类型路线时触发）
    const { data: handoverCarriersData } = useQuery<GetHandoverCarriersByWarehouseIdResponse>(GET_HANDOVER_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : 0,
            // 带出路线默认交接方（即使已禁用或移出本仓），保证回显不只剩裸 id
            includeCarrierId: selectedRoute?.carrierId != null ? Number(selectedRoute.carrierId) : undefined
        },
        skip: !isHandoverRoute || !currentWarehouse?.id
    });
    const handoverCarrierOptions = useMemo(() => {
        const list = handoverCarriersData?.getHandoverCarriersByWarehouseId ?? [];
        // 按路线的交接方类型(carrierSourceType: 1-本地尾程商, 2-干线承运商)过滤，避免列出不匹配类型的交接方
        const handoverType = selectedRoute?.carrierSourceType;
        const filtered = handoverType != null ? list.filter(hc => hc.carrierType === handoverType) : list;
        const warehouseId = currentWarehouse?.id ? String(currentWarehouse.id) : '';
        return filtered.map(hc => {
            // 不可用：已禁用 或 已移出本仓；置灰 + 标「不可用」，可回显但不可重新选择
            const isActive = hc.status === TmsStatusEnum.Active;
            const inWarehouse = hc.centerWarehouseIds.includes(ALL_CENTER_WAREHOUSE_ID) || hc.centerWarehouseIds.includes(warehouseId);
            const unavailable = !isActive || !inWarehouse;
            return {
                label: hc.carrierName,
                value: hc.id,
                disabled: unavailable,
                secondaryLabel: unavailable ? String(t('tms.route.drawer.unavailableTag')) : undefined,
            };
        });
    }, [handoverCarriersData, selectedRoute?.carrierSourceType, currentWarehouse]);

    // 获取承运商/交接方名称（直接从已有列表数据中查找，无需额外查询）
    const carrierName = isHandoverRoute
        ? (handoverCarriersData?.getHandoverCarriersByWarehouseId?.find(hc => hc.id === selectedCarrierId)?.carrierName || '')
        : (allCarriersData?.getTmsCarriersByWarehouseId?.find(c => String(c.id) === selectedCarrierId)?.carrierName || '');


    // 根据路线类型过滤承运商（选择路线后才显示承运商选项）
    const carriersForFilter: TmsCarrierType[] | undefined = selectedRoute ? allCarriersData?.getTmsCarriersByWarehouseId : [];
    const tmsCarrierOptions = useFilteredCarriers(
        carriersForFilter,
        selectedRoute?.routeType
    );

    // 交接类型用 handoverCarrierOptions，其他用 tmsCarrierOptions
    const carrierOptions = isHandoverRoute ? handoverCarrierOptions : tmsCarrierOptions;

    const carrierTypeOptions = useMemo(() => {
        if (isHandoverRoute) {
            return [
                { label: String(t('tms.route.handoverTypes.localLastMileCarrier')), value: String(HandoverTypeEnum.LocalLastMileCarrier) },
                { label: String(t('tms.route.handoverTypes.linehaulCarrier')), value: String(HandoverTypeEnum.LinehaulCarrier) },
            ];
        }
        return [
            { label: String(t('tms.task.drawer.carrierTypeOptions.internal')), value: '1' },
            { label: String(t('tms.task.drawer.carrierTypeOptions.thirdParty')), value: '2' },
        ];
    }, [isHandoverRoute, t]);

    // 当路线类型改变时，验证当前选择的承运商是否仍然有效
    useEffect(() => {
        if (isHandoverRoute) return;
        if (selectedCarrierId && selectedRoute?.routeType && allCarriersData?.getTmsCarriersByWarehouseId) {
            const isValid = isCarrierValidForRouteType(
                allCarriersData.getTmsCarriersByWarehouseId,
                selectedCarrierId,
                selectedRoute.routeType
            );
            if (!isValid) {
                // 如果承运商不再有效，清空选择
                setSelectedCarrierId('');
            }
        }
    }, [selectedRoute?.routeType, selectedCarrierId, allCarriersData, isHandoverRoute]);

    // 根据 vehicleId 获取对应的车辆信息（vehicleModelName、vehicleModelId）
    const selectedVehicle = useMemo(() => {
        if (!filters.vehicleId || !vehiclesData?.getTmsVehiclesWithModel) return null;
        return vehiclesData.getTmsVehiclesWithModel.find(v => v.id.toString() === filters.vehicleId.toString()) || null;
    }, [filters.vehicleId, vehiclesData]);

    // 当手动选择 vehicle 时，自动填充 vehicleModel 和 vehicleTypeId
    useEffect(() => {
        if (!isVehicleAutoSet && filters.vehicleId && selectedVehicle) {
            setFilters(prev => ({
                ...prev,
                vehicleModel: selectedVehicle.vehicleTypeName || '',
                vehicleTypeId: selectedVehicle.vehicleTypeId || null,
            }));
        }
    }, [filters.vehicleId, selectedVehicle, isVehicleAutoSet]);

    // 验证表单字段（含司机1 联系方式的必填、格式与重号检测）
    const validateForm = async (): Promise<boolean> => {
        if (!filters.routeId) {
            message.error(t('tms.task.messages.pleaseSelectRoute'));
            return false;
        }

        if (!filters.taskDate) {
            message.error(t('tms.task.messages.pleaseSelectTaskDate'));
            return false;
        }
        if (!filters.originPlannedDepartureTime) {
            message.error(t('tms.task.messages.pleaseSelectDepartureTime'));
            return false;
        }
        
        if (!selectedCarrierId) {
            message.error(t('tms.task.messages.pleaseSelectCarrier'));
            return false;
        }

        if (shouldIncludePerVehicleCost) {
            if (!filters.perVehicleCost?.trim()) {
                message.error(t('tms.task.messages.pleaseInputPerVehicleCost'));
                return false;
            }
            const perVehicleCostCents = parseDecimalStringToCents(filters.perVehicleCost);
            if (perVehicleCostCents < 0) {
                message.error(t('tms.task.messages.perVehicleCostCannotBeNegative'));
                return false;
            }
        }

        if (!filters.escortCount?.trim()) {
            message.error(t('tms.task.messages.pleaseInputEscortCount'));
            return false;
        }

        // 非交接类型：司机、车辆、车型必填
        if (!isHandoverRoute) {
            // 司机名称必填（可以是选择的系统司机或输入的临时司机）
            if (!filters.driver1Name) {
                message.error(t('tms.task.messages.pleaseSelectDriver1'));
                return false;
            }
            // 车辆名称必填（可以是选择的车辆或自由输入）
            if (!filters.licensePlate) {
                message.error(t('tms.task.messages.pleaseSelectVehicle'));
                return false;
            }
            // 车型必填（选择车辆时应自动回填；车辆自由输入时需手动选择）
            if (!filters.vehicleTypeId) {
                message.error(t('tms.task.messages.pleaseSelectVehicleModel'));
                return false;
            }
        }
        if (!isHandoverRoute && !filters.trailerNumber?.trim()) {
            message.error(t('tms.task.messages.pleaseInputTrailerNumber'));
            return false;
        }
       
        if (!selectedRoute?.startWarehouseId) {
            message.error(t('tms.task.messages.failedToGetWarehouseInformation'));
            return false;
        }
        // 联系方式校验：非交接必填，交接非必填；两者都要求填了就是 10 位
        if (!isHandoverRoute && !filters.driver1PhoneNumber) {
            message.error(t('tms.task.messages.pleaseInputDriver1Phone'));
            return false;
        }
        if (isPhoneInvalid(filters.driver1PhoneNumber)) {
            message.error(t('tms.task.messages.pleaseInputValidPhoneNumber'));
            return false;
        }
        // 重号检测：司机1、司机2 同一套口径，是否要检测由各自的 validate 方法内部判断
        const phoneError = await validateDriver1PhoneAsync(filters.driver1PhoneNumber);
        if (phoneError) {
            message.error(phoneError);
            return false;
        }
        // 司机2 非必填，跟车人数<=1 时不展示，跳过校验；填了就必须是 10 位
        if (showDriver2) {
            if (isPhoneInvalid(filters.driver2PhoneNumber)) {
                message.error(t('tms.task.messages.pleaseInputValidPhoneNumber'));
                return false;
            }
            const driver2Error = await validateDriver2PhoneAsync(filters.driver2PhoneNumber);
            if (driver2Error) {
                message.error(driver2Error);
                return false;
            }
        }
        // 转运校验：选"是"时，每行是"转运目的地站 + 转运站"配对。空行忽略；半填行(只填一列)报错需成对；至少要有一组完整配对
        if (showTransit && isTransit === IS_TRANSIT.YES) {
            const hasPartial = transitRows.some((r) => !!r.transitDestWarehouseId !== !!r.transitWarehouseId);
            if (hasPartial) {
                message.error(t('tms.task.drawer.transitTable.incompletePair'));
                return false;
            }
            const hasComplete = transitRows.some((r) => r.transitDestWarehouseId && r.transitWarehouseId);
            if (!hasComplete) {
                message.error(t('tms.task.drawer.transitTable.required'));
                return false;
            }
        }
        return true;
    };

    // 转运信息三态：非 showTransit → 不传；"否" → 不传；"是" → 过滤出完整配对的行
    const getTransitInfosInput = (): TmsTaskTransitInfoInput[] | undefined => {
        if (!showTransit || isTransit !== IS_TRANSIT.YES) return undefined;
        return transitRows
            .filter((r) => r.transitWarehouseId && r.transitDestWarehouseId)
            .map((r) => ({
                transitWarehouseId: Number(r.transitWarehouseId),
                transitDestWarehouseId: Number(r.transitDestWarehouseId),
            }));
    };

    // 构建任务输入对象（doSubmit / handleConfirmSubmit 共用）
    const buildTaskInput = (overrides?: {
        driverId?: number;
        vehicleId?: number;
        temporaryDriver1?: TmsTaskTemporaryDriverInput;
        temporaryDriver2?: TmsTaskTemporaryDriverInput;
        temporaryVehicle?: TmsTaskTemporaryVehicleInput;
    }): CreateTmsTaskInput => ({
        taskSource: TmsTaskSourceEnum.Manual,
        businessType: Number(selectedRoute?.businessType),
        taskType: TmsTaskTypeEnum.Temporary,
        taskDate: filters.taskDate,
        taskStatus: TmsTaskStatusEnum.Draft,
        routeId: parseInt(filters.routeId),
        routeName: selectedRoute!.routeName,
        warehouseId: Number(selectedRoute!.startWarehouseId),
        planDepartureDate: filters.planDepartureDate,
        planDepartureTime: filters.originPlannedDepartureTime,
        carrierId: Number(selectedCarrierId!),
        driver1Id: overrides?.driverId ?? (filters.driver1Id ? parseInt(filters.driver1Id) : undefined),
        driver1Name: filters.driver1Name || null,
        driver1PhoneNumber: filters.driver1PhoneNumber,
        // 司机2：非必填，仅BOL展示；跟车人数<=1 时不展示司机2，强制置空，不透传 filters 里的残留值
        driver2Id: showDriver2 && filters.driver2Id ? parseInt(filters.driver2Id) : null,
        driver2Name: showDriver2 ? (filters.driver2Name || null) : null,
        driver2PhoneNumber: showDriver2 ? (filters.driver2PhoneNumber || null) : null,
        vehicleId: overrides?.vehicleId ?? (filters.vehicleId ? parseInt(filters.vehicleId) : undefined),
        temporaryDriver1: overrides?.temporaryDriver1,
        temporaryDriver2: showDriver2 ? overrides?.temporaryDriver2 : undefined,
        temporaryVehicle: overrides?.temporaryVehicle,
        trailerNumber: filters.trailerNumber || null,
        escortCount: Number.parseInt(filters.escortCount || '0', 10),
        perVehicleCost: shouldIncludePerVehicleCost ? parseDecimalStringToCents(filters.perVehicleCost) : null,
        gpsTracking: filters.gpsTracking.trim() || null,
        licensePlate: filters.licensePlate || null,
        vehicleTypeId: filters.vehicleTypeId || null,
        remark: filters.remark.trim() || null,
        transitInfos: getTransitInfosInput(),
        isBulkConsolidated,
    });

    // 实际提交任务（司机2 电话的格式与重号校验已在 validateForm 内完成）
    const doSubmit = async () => {
        try {
            const res = (await createTemporaryTask({ variables: { input: buildTaskInput() } })).data?.createTmsTask;
            if (!res?.success) {
                // 本流程校验失败以结果码返回（不抛异常）：提示后保留抽屉，便于修改后重新提交
                message.error(resultErrorMessage(res, t('tms.task.messages.failedToCreateTemporaryTask')));
                return;
            }
            message.success(t('tms.task.messages.temporaryTaskCreatedSuccessfully'));
            onSubmit();
            handleClose();
        } catch (error: any) {
            console.error('Failed to create temporary task:', error);
            const hasMessageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
            hasMessageKey ? showError(error) : message.error(error?.message || t('tms.task.messages.failedToCreateTemporaryTask'));
        }
    };

    // 处理提交按钮点击
    const handleSubmit = async () => {
        // 先验证表单（含联系方式 rules 校验）
        if (!(await validateForm())) {
            return;
        }

        // 检查是否需要显示确认弹窗（交接类型不需要）；手输司机2 也要建号，同样弹
        if (!isHandoverRoute && (isDriver1FreeInput || isDriver2FreeInput || isVehicleFreeInput)) {
            // 自由编辑车辆时：弹窗前校验车牌是否已存在且绑定其他承运商
            if (isVehicleFreeInput && selectedCarrierId) {
                const licensePlate = (filters.licensePlate || '').trim();
                if (licensePlate) {
                    try {
                        const { data } = await getVehiclesByLicensePlate({
                            variables: { licensePlate }
                        });
                        const list = data?.allVehicles ?? [];
                        const currentCarrierId = Number(selectedCarrierId);
                        const boundToOther = list.find((v: { carrierId: number; licensePlate?: string; vehicleNumber?: string; carrierName?: string }) =>
                            (v.licensePlate === licensePlate || v.vehicleNumber === licensePlate) &&
                            v.carrierId !== currentCarrierId
                        );
                        if (boundToOther) {
                            Modal.error({
                                title: t('tms.task.messages.vehicleBoundToOtherCarrierTitle'),
                                content: t('tms.task.messages.vehicleBoundToOtherCarrierContent', {
                                    licensePlate: boundToOther.licensePlate || boundToOther.vehicleNumber || licensePlate,
                                    carrierName: boundToOther.carrierName || ''
                                })
                            });
                            return;
                        }
                    } catch (e) {
                        console.error('Check vehicle by license plate failed:', e);
                    }
                }
            }
            // 重置表单并设置初始值（只在弹窗未打开时重置，避免覆盖用户已修改的值）
            if (isDriver1FreeInput && !confirmModalVisible) {
                driver1Form.resetFields();
                driver1Form.setFieldsValue({
                    name: filters.driver1Name,
                    phone: {
                        countryCode: 1,
                        phoneNumber: filters.driver1PhoneNumber || ''
                    },
                    role: 'TMS_DRIVER',
                    status: 1,
                    // 车辆为自由输入时，仅在司机信息里展示车牌号，不设置 vehicleId
                    ...(isVehicleFreeInput
                        ? { vehicleDisplay: filters.licensePlate || '' }
                        : { vehicleId: filters.vehicleId ? Number(filters.vehicleId) : undefined })
                });
            }
            // 司机2 弹窗表单：字段同司机1，但不绑定车辆，故无车辆字段
            if (isDriver2FreeInput && !confirmModalVisible) {
                driver2Form.resetFields();
                driver2Form.setFieldsValue({
                    name: filters.driver2Name,
                    phone: {
                        countryCode: 1,
                        phoneNumber: filters.driver2PhoneNumber || ''
                    },
                    role: 'TMS_DRIVER',
                    status: 1
                });
            }
            if (isVehicleFreeInput && !confirmModalVisible) {
                vehicleForm.resetFields();
                const matchedModel = vehicleModelOptions.find(o => o.label === filters.vehicleModel);
                vehicleForm.setFieldsValue({
                    licensePlate: filters.licensePlate,
                    status: 1,
                    vehicleTypeId: matchedModel?.value
                });
            }
            setConfirmModalVisible(true);
        } else {
            // 不需要确认，直接提交
            await doSubmit();
        }
    };

    // 确认弹窗中车型选择变化回调，通过 findVehicleTypeById 获取默认物理属性并填入车辆表单
    const handleVehicleTypeChange = async (vehicleTypeId: number) => {
        try {
            const result = await findVehicleTypeById({ variables: { id: vehicleTypeId } });
            const vehicleType = result.data?.findVehicleTypeById;
            if (vehicleType) {
                vehicleForm.setFieldsValue({
                    maxWeightLbs: vehicleType.defaultMaxWeightLbs ?? undefined,
                    maxVolumeFt3: vehicleType.defaultMaxVolumeFt3 ?? undefined,
                    lengthFt: vehicleType.defaultLengthFt ?? undefined,
                    widthFt: vehicleType.defaultWidthFt ?? undefined,
                    heightFt: vehicleType.defaultHeightFt ?? undefined,
                });
            }
        } catch (error) {
            console.error('Failed to fetch vehicle type details:', error);
        }
    };

    // 确认弹窗确认后的提交（临时司机/车辆通过 createTmsTask 的 temporaryDriver1/temporaryVehicle 一并提交）
    const handleConfirmSubmit = async () => {
        setConfirmSubmitting(true);
        try {
            let temporaryDriver1: TmsTaskTemporaryDriverInput | undefined;
            let temporaryDriver2: TmsTaskTemporaryDriverInput | undefined;
            let temporaryVehicle: TmsTaskTemporaryVehicleInput | undefined;

            if (isDriver1FreeInput) {
                const driverValues = await driver1Form.validateFields();
                temporaryDriver1 = {
                    name: driverValues.name,
                    countryCode: driverValues.phone?.countryCode ?? 1,
                    phone: driverValues.phone?.phoneNumber || undefined,
                    password: driverValues.password || undefined,
                    email: driverValues.email || undefined,
                    role: 'TMS_DRIVER',
                    status: driverValues.status ?? 1,
                    carrierId: Number(selectedCarrierId!),
                    vehicleId: driverValues.vehicleId ? Number(driverValues.vehicleId) : undefined
                };
            }

            // 手输司机2 也建账号（不参与派单），承运商同司机1；不传 vehicleId——司机2 不绑定车辆
            if (isDriver2FreeInput) {
                const driver2Values = await driver2Form.validateFields();
                temporaryDriver2 = {
                    name: driver2Values.name,
                    countryCode: driver2Values.phone?.countryCode ?? 1,
                    phone: driver2Values.phone?.phoneNumber || undefined,
                    password: driver2Values.password || undefined,
                    email: driver2Values.email || undefined,
                    role: 'TMS_DRIVER',
                    status: driver2Values.status ?? 1,
                    carrierId: Number(selectedCarrierId!)
                };
            }

            if (isVehicleFreeInput) {
                const vehicleValues = await vehicleForm.validateFields();
                temporaryVehicle = {
                    licensePlate: vehicleValues.licensePlate,
                    carrierId: Number(selectedCarrierId!),
                    vehicleTypeId: Number(vehicleValues.vehicleTypeId),
                    maxWeightLbs: vehicleValues.maxWeightLbs ?? undefined,
                    maxVolumeFt3: vehicleValues.maxVolumeFt3 ?? undefined,
                    lengthFt: vehicleValues.lengthFt ?? undefined,
                    widthFt: vehicleValues.widthFt ?? undefined,
                    heightFt: vehicleValues.heightFt ?? undefined,
                    fuelType: vehicleValues.fuelType ?? undefined,
                    status: vehicleValues.status ?? 1,
                    vehiclePhotos: vehicleValues.vehiclePhotos?.length ? vehicleValues.vehiclePhotos : undefined
                };
            }

            setConfirmModalVisible(false);

            const input = buildTaskInput({
                driverId: !isDriver1FreeInput && filters.driver1Id ? parseInt(filters.driver1Id) : undefined,
                vehicleId: !isVehicleFreeInput && filters.vehicleId ? parseInt(filters.vehicleId) : undefined,
                temporaryDriver1,
                temporaryDriver2,
                temporaryVehicle,
            });

            const res = (await createTemporaryTask({ variables: { input } })).data?.createTmsTask;
            if (!res?.success) {
                // 本流程校验失败以结果码返回（不抛异常）：提示后保留抽屉，便于修改后重新提交
                message.error(resultErrorMessage(res, t('tms.task.messages.failedToCreateTemporaryTask')));
                return;
            }
            message.success(t('tms.task.messages.temporaryTaskCreatedSuccessfully'));
            onSubmit();
            handleClose();
        } catch (error: any) {
            console.error('Failed to create:', error);
            if (error.errorFields) {
                // 表单验证错误，不显示额外消息
            } else {
                const hasMessageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
                hasMessageKey ? showError(error) : message.error(error?.message || t('tms.task.messages.failedToCreateTemporaryTask'));
            }
        } finally {
            setConfirmSubmitting(false);
        }
    };

    // 获取确认弹窗标题
    const getConfirmModalTitle = (): string => {
        // 要建号的司机可能是司机1、司机2 或两个，姓名拼在同一个插值里，不为此新增文案
        const driverName = [
            isDriver1FreeInput ? filters.driver1Name : '',
            isDriver2FreeInput ? filters.driver2Name : '',
        ].filter(Boolean).join('、');
        if (driverName && isVehicleFreeInput) {
            return String(t('tms.task.confirmModal.titleBoth', {
                driverName,
                licensePlate: filters.licensePlate
            }));
        } else if (driverName) {
            return String(t('tms.task.confirmModal.titleDriver', {
                driverName
            }));
        } else if (isVehicleFreeInput) {
            return String(t('tms.task.confirmModal.titleVehicle', {
                licensePlate: filters.licensePlate
            }));
        }
        return '';
    };

    const handleFilterChange = (name: keyof FilterOptions, value: string | number | undefined | string[] | number[]) => {
        setFilters(prev => ({ ...prev, [name]: value || '' }));
    };

    // 任务日期变更时，校验已选出发日期是否在合法范围内，不合法则清空
    const clearDepartureTimeIfOutOfRange = (newTaskDate: string) => {
        if (departureTime && newTaskDate) {
            if (isDayOutOfRange(toDay(departureTime), getDepartureDateRange(toDay(dayjs()), newTaskDate))) {
                setDepartureTime(null);
                setFilters(prev => ({ ...prev, planDepartureDate: '', originPlannedDepartureTime: '' }));
            }
        } else if (!newTaskDate) {
            setDepartureTime(null);
            setFilters(prev => ({ ...prev, planDepartureDate: '', originPlannedDepartureTime: '' }));
        }
    };

    const resetDependentFields = (newBusinessType?: string, clearCarrierType = false) => {
        setFilters(prev => ({
            ...prev,
            businessType: newBusinessType ?? prev.businessType,
            routeId: '',
            driver1Id: '',
            driver1Name: '',
            driver1PhoneNumber: '',
            driver2Id: '',
            driver2Name: '',
            driver2PhoneNumber: '',
            vehicleId: '',
            licensePlate: '',
            vehicleModel: '',
            vehicleTypeId: null,
        }));
        setSelectedCarrierId('');
        setRouteSelectKey(prev => prev + 1);
        setIsVehicleAutoSet(false);
                setIsVehicleFreeInput(false);
        setIsDriver1PhoneAutoFilled(false);
        setIsDriver2PhoneAutoFilled(false);
        setIsDriver1FreeInput(false);
        setDriver2TypedByUser(false);
        businessTypeSyncedRouteIdRef.current = null;
    };

    const handleBusinessTypeChange = (value: string | number | null) => {
        const normalizedValue = value ? String(value) : '';
        resetDependentFields(normalizedValue, true);
    };

    // 清空车辆相关字段（司机无绑定车辆 / 查询出错时共用）
    const clearVehicleFields = () => {
        setFilters(prev => ({ ...prev, vehicleId: '', licensePlate: '', vehicleModel: '', vehicleTypeId: null }));
        setIsVehicleAutoSet(false);
        setIsVehicleFreeInput(false);
    };

    // 使用Hook查询车辆和车型信息
    const { getVehicleAndModel } = useTmsVehicleAndModel({
        onVehicleAndModelFound: (vehicleId, vehicleModelName, _vehicleModelId, vehicle, vehicleTypeId) => {
            setFilters(prev => ({
                ...prev,
                vehicleId: vehicleId.toString(),
                licensePlate: vehicle.licensePlate || '',
                vehicleModel: vehicleModelName,
                vehicleTypeId: vehicleTypeId || null,
            }));
            setIsVehicleAutoSet(true);
            setIsVehicleFreeInput(false);
        },
        onVehicleAndModelNotFound: clearVehicleFields,
        onError: clearVehicleFields,
    });

    return (
        <EditableDrawer
            title={drawerTitle}
            visible={open}
            onClose={handleClose}
            onSubmit={handleSubmit}
            confirmDisabled={creating}
            confirmLoading={creating}
            width={800}
        >
            <div className="bg-white mb-4">
                <div className="flex gap-4">
                    <div className="flex-1">
                        <div className="text-black font-bold text-base mb-4">
                            {String(t('tms.task.drawer.routeInfo'))}
                        </div>
                        <div className="mb-4">
                            <SingleSelect
                                label={String(t('tms.task.filters.businessType'))}
                                placeholder={String(t('tms.task.drawer.selectBusinessType'))}
                                options={businessTypeOptions}
                                value={filters.businessType}
                                onChange={handleBusinessTypeChange}
                                spanClassName="w-[7em]"
                                required
                                allowClear
                                forceHorizontal
                            />
                        </div>
                        <div className="mb-4">
                            <SearchSelect
                                key={`route-select-${routeSelectKey}`}
                                label={String(t('tms.task.drawer.route'))}
                                placeholder={String(t('tms.task.drawer.searchRoute'))}
                                value={filters.routeId}
                                onChange={(value) => {
                                    setFilters(prev => ({
                                        ...prev,
                                        routeId: (value || '') as string,
                                        driver1Id: '',
                                        driver1Name: '',
                                        driver1PhoneNumber: '',
                                        driver2Id: '',
                                        driver2Name: '',
                                        driver2PhoneNumber: '',
                                        vehicleId: '',
                                        licensePlate: '',
                                        vehicleModel: '',
                                        vehicleTypeId: null,
                                    }));
                                    setIsVehicleFreeInput(false);
                                    setIsDriver1FreeInput(false);
                                    setIsDriver1PhoneAutoFilled(false);
                                    // 路线变化后，此前选择的转运站可能不再合法，重置转运配置
                                    setIsTransit(IS_TRANSIT.NO);
                                    setTransitRows([]);
                                    businessTypeSyncedRouteIdRef.current = null;
                                }}
                                onSearch={(searchValue, callback) => handleRouteSearch(
                                    searchValue,
                                    callback,
                                    TmsStatusEnum.Active,
                                    false,
                                    undefined,
                                    [WaypointTypeEnum.Origin],
                                    filters.businessType ? Number(filters.businessType) : undefined
                                )}
                                spanClassName="w-[7em]"
                                required
                                forceHorizontal
                            />
                        </div>
                        <div className="mb-4">
                            <DatePickerFilter
                                label={String(t('tms.task.drawer.taskDate'))}
                                value={filters.taskDate ? dayjs(filters.taskDate) : null}
                                onChange={(date) => {
                                    const newTaskDate = date ? date.format('YYYY-MM-DD') : '';
                                    handleFilterChange('taskDate', newTaskDate);
                                    clearDepartureTimeIfOutOfRange(newTaskDate);
                                }}
                                spanClassName="w-[7em]"
                                format="YYYY-MM-DD"
                                placeholder={String(t('tms.task.drawer.selectDate'))}
                                allowClear
                                disabledDate={(current) => {
                                    if (!current) return false;
                                    // 已选出发日期时，taskDate 只能是 departureDate-1 或 departureDate
                                    const range = getTaskDateRange(toDay(dayjs()), departureTime ? toDay(departureTime) : null);
                                    return isDayOutOfRange(toDay(current), range);
                                }}
                                required
                                forceHorizontal
                            />
                        </div>
                        <div className="mb-4">
                            <DatePickerFilter
                                label={String(t('tms.task.drawer.departureTime'))}
                                value={departureTime}
                                onChange={(dateTime) => {
                                    setDepartureTime(dateTime);
                                    handleFilterChange('planDepartureDate', dateTime ? dateTime.format('YYYY-MM-DD') : '');
                                    handleFilterChange('originPlannedDepartureTime', dateTime ? dateTime.format('HH:mm') : '');
                                }}
                                spanClassName="w-[7em]"
                                format="YYYY-MM-DD HH:mm"
                                placeholder={String(t('tms.task.drawer.selectDepartureTime'))}
                                showTime={{ format: 'HH:mm', minuteStep: 5 }}
                                disabledDate={(current) => {
                                    // 已选任务日期：出发日期可选 max(taskDate, today) ~ min(taskDate+1, today+3)
                                    // 未选任务日期：出发日期可选 today ~ today+3
                                    const range = getDepartureDateRange(toDay(dayjs()), filters.taskDate || null);
                                    return isDayOutOfRange(toDay(current), range);
                                }}
                                required
                                forceHorizontal
                            />
                        </div>
                        <div className="mb-4">
                            <SingleSelect
                                label={String(t('tms.task.drawer.taskType'))}
                                options={taskTypeOptions}
                                value={filters.taskType ? filters.taskType.toString() : ''}
                                onChange={value => handleFilterChange('taskType', value)}
                                spanClassName="w-[7em]"
                                required
                                disabled
                                forceHorizontal
                            />
                        </div>
                        <div className={`mb-4 ${containerClassName}`}>
                            <span className={labelClassName}>
                                {String(t('tms.task.drawer.isBulkConsolidated'))}
                                <span className="text-red-500 ml-1">*</span>
                            </span>
                            <div className={inputContainerClassName}>
                                <Radio.Group
                                    value={isBulkConsolidated}
                                    onChange={(e) => setIsBulkConsolidated(e.target.value)}
                                    optionType="button"
                                    buttonStyle="solid"
                                >
                                    <Radio.Button value={true}>{String(t('common.yes'))}</Radio.Button>
                                    <Radio.Button value={false}>{String(t('common.no'))}</Radio.Button>
                                </Radio.Group>
                            </div>
                        </div>
                        {showTransit && (
                            <>
                                <div className={`mb-4 ${containerClassName}`}>
                                    <span className={labelClassName}>
                                        {String(t('tms.task.drawer.isTransit'))}
                                        <span className="text-red-500 ml-1">*</span>
                                    </span>
                                    <div className={inputContainerClassName}>
                                        <Radio.Group
                                            value={isTransit}
                                            onChange={(e) => handleIsTransitChange(e.target.value === IS_TRANSIT.YES)}
                                            optionType="button"
                                            buttonStyle="solid"
                                        >
                                            <Radio.Button value={IS_TRANSIT.YES}>{String(t('common.yes'))}</Radio.Button>
                                            <Radio.Button value={IS_TRANSIT.NO}>{String(t('common.no'))}</Radio.Button>
                                        </Radio.Group>
                                    </div>
                                </div>
                                {isTransit === IS_TRANSIT.YES && (
                                    <>
                                        <div className="flex">
                                            {/* 空出与 label 等宽(w-[7em])的占位，使表格与输入区对齐 */}
                                            <div className="w-[7em] flex-shrink-0 mr-2" />
                                            <TransitInfoTable
                                                dataSource={transitRows}
                                                onChange={setTransitRows}
                                                transitStationOptions={transitStationOptions}
                                                transitDestStationOptions={transitDestStationOptions}
                                            />
                                        </div>
                                        <div className="mb-4 flex">
                                            <div className="w-[7em] flex-shrink-0 mr-2" />
                                            {/* 转运填写示例提示（主题紫色），与编辑任务页样式一致 */}
                                            <div className="text-purple-600">
                                                {String(t('tms.task.drawer.transitTable.hint'))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        <div className="text-black font-bold text-base mt-4 mb-4">
                            {String(t(isHandoverRoute ? 'tms.task.drawer.handoverCarrierInfo' : 'tms.task.drawer.carrierInfo'))}
                        </div>
                        {isHandoverRoute && (
                        <div className="mb-4">
                            <SingleSelect
                                label={String(t('tms.schedule.drawer.handoverCarrierType'))}
                                placeholder={String(t('tms.task.drawer.autoFilledFromRoute'))}
                                options={carrierTypeOptions}
                                value={selectedRoute?.carrierSourceType != null ? String(selectedRoute.carrierSourceType) : ''}
                                onChange={() => {}}
                                spanClassName="w-[7em]"
                                required
                                disabled
                                allowClear={false}
                                forceHorizontal
                            />
                        </div>
                        )}
                        <div className="mb-4">
                            <SingleSelect
                                label={String(t(isHandoverRoute ? 'tms.schedule.drawer.handoverCarrier' : 'tms.task.drawer.carrier'))}
                                placeholder={String(t('tms.task.drawer.autoFilledFromRoute'))}
                                options={carrierOptions}
                                value={selectedCarrierId}
                                onChange={(value) => setSelectedCarrierId(value ?? '')}
                                spanClassName="w-[7em]"
                                required
                                forceHorizontal
                            />
                        </div>
                        {/* 单车成本：业务来源为非自有路线 且 承运商类型为三方时不显示 */}
                        {shouldIncludePerVehicleCost && (
                            <div className="mb-4">
                                <InputFilterItem
                                    label={String(t('tms.task.drawer.perVehicleCost'))}
                                    placeholder={String(t('tms.task.drawer.placeholderPerVehicleCost'))}
                                    value={filters.perVehicleCost}
                                    onChange={(e) => handleFilterChange('perVehicleCost', sanitizeDecimalInput(e.target.value))}
                                    spanClassName="w-[7em]"
                                    forceHorizontal
                                    required
                                />
                            </div>
                        )}
                        <div className="mb-4">
                            <SingleSelect
                                label={String(t('tms.task.drawer.escortCount'))}
                                placeholder={String(t('tms.task.drawer.selectEscortCount'))}
                                options={escortCountOptions}
                                value={filters.escortCount}
                                onChange={(value) => {
                                    const nextValue = value ?? '';
                                    handleFilterChange('escortCount', nextValue);
                                    // 跟车人数改回1：清空司机2，避免隐藏后残留数据被提交
                                    if (Number(nextValue) <= 1) {
                                        setFilters(prev => ({ ...prev, driver2Id: '', driver2Name: '', driver2PhoneNumber: '' }));
                                        setIsDriver2PhoneAutoFilled(false);
                                        setDriver2TypedByUser(false);
                                        setDriver2PhoneError(null);
                                    }
                                }}
                                spanClassName="w-[7em]"
                                forceHorizontal
                                required
                                allowClear={false}
                            />
                        </div>
                        <DriverPhoneRow
                            labels={String(t('tms.task.drawer.driver1AndPhone'))}
                            required={!isHandoverRoute}
                            freeInputOnly={isHandoverRoute}
                            driverOptions={driver1Options}
                            driverPhoneById={driverPhoneById}
                            value={{
                                driverId: filters.driver1Id,
                                driverName: filters.driver1Name,
                                phoneNumber: filters.driver1PhoneNumber,
                            }}
                            onChange={(v) => setFilters(prev => ({
                                ...prev,
                                driver1Id: v.driverId,
                                driver1Name: v.driverName,
                                driver1PhoneNumber: v.phoneNumber,
                            }))}
                            namePlaceholder={String(t(isHandoverRoute ? 'tms.task.drawer.inputDriver1' : 'tms.task.drawer.selectDriver1'))}
                            phonePlaceholder={String(t('tms.task.drawer.driver1PhonePlaceholder'))}
                            phoneDisabled={isDriver1PhoneAutoFilled}
                            phoneError={driver1PhoneError}
                            onPhoneFocus={() => setDriver1PhoneError(null)}
                            onPhoneBlur={validateDriver1PhoneAsync}
                            containerClassName={`mb-4 ${containerClassName}`}
                            labelClassName={labelClassName}
                            inputContainerClassName={inputContainerClassName}
                            onDriverSelected={(driverId, _name, phoneNumber) => {
                                // 从列表选中司机：清空车辆/车牌/车型，由所选司机重新带出
                                setFilters(prev => ({
                                    ...prev,
                                    vehicleId: '',
                                    licensePlate: '',
                                    vehicleModel: '',
                                    vehicleTypeId: null,
                                }));
                                setIsVehicleFreeInput(false);
                                setIsDriver1FreeInput(false);
                                setIsDriver1PhoneAutoFilled(!!phoneNumber);
                                setDriver1PhoneError(null);
                                getVehicleAndModel({ variables: { driverId: parseInt(driverId) } });
                            }}
                            onDriverCleared={() => {
                                // 输入的司机不在列表中（临时司机）
                                setFilters(prev => ({
                                    ...prev,
                                    vehicleId: '',
                                    licensePlate: '',
                                    vehicleModel: '',
                                    vehicleTypeId: null,
                                    // 自动填充的号码要清掉，用户手输的保留
                                    driver1PhoneNumber: isDriver1PhoneAutoFilled ? '' : prev.driver1PhoneNumber,
                                }));
                                setIsVehicleAutoSet(false);
                                setIsVehicleFreeInput(false);
                                setIsDriver1PhoneAutoFilled(false);
                                setIsDriver1FreeInput(true);
                                setDriver1PhoneError(null);
                            }}
                        />
                        {/* 司机2&电话：跟车人数>1才展示；非必填，仅BOL展示，不参与派单/APP/结算；可选系统司机，也可自由输入姓名 */}
                        {showDriver2 && (
                        <DriverPhoneRow
                            labels={String(t('tms.task.drawer.driver2AndPhone'))}
                            driverOptions={driver2Options}
                            driverPhoneById={driverPhoneById}
                            value={{
                                driverId: filters.driver2Id,
                                driverName: filters.driver2Name,
                                phoneNumber: filters.driver2PhoneNumber,
                            }}
                            onChange={(v) => {
                                // 司机2 从「所选系统司机」改成手输：该司机带出的电话要跟着清掉（手输的电话保留）。
                                // 只动电话——司机2 不关联车辆/车型，不像司机1 那样联动清空
                                const droppedSystemDriver = !v.driverId && !!filters.driver2Id;
                                if (v.driverId || droppedSystemDriver) setDriver2PhoneError(null);
                                // 改成手输即解灰；置灰只在 onDriverSelected 里判一次——放在这里会导致
                                // 「已选系统司机但电话为空」时，用户手输第一个数字就被置灰
                                if (droppedSystemDriver) setIsDriver2PhoneAutoFilled(false);
                                setFilters(prev => ({
                                    ...prev,
                                    driver2Id: v.driverId,
                                    driver2Name: v.driverName,
                                    driver2PhoneNumber: droppedSystemDriver ? '' : v.phoneNumber,
                                }));
                            }}
                            freeInputOnly={isHandoverRoute}
                            // 选中系统司机且带出了电话才置灰（与司机1 同口径）
                            onDriverSelected={(_driverId, _name, phoneNumber) => {
                                setDriver2TypedByUser(false);
                                setIsDriver2PhoneAutoFilled(!!phoneNumber);
                            }}
                            // 手打姓名即视为手输司机2（交接类型 freeInputOnly，组件不会触发此回调）
                            onDriverCleared={() => {
                                setDriver2TypedByUser(true);
                                setIsDriver2PhoneAutoFilled(false);
                                setDriver2PhoneError(null);
                            }}
                            namePlaceholder={String(t(isHandoverRoute ? 'tms.task.drawer.inputDriver2' : 'tms.task.drawer.selectDriver2'))}
                            phonePlaceholder={String(t('tms.task.drawer.driver2PhonePlaceholder'))}
                            phoneDisabled={isDriver2PhoneAutoFilled}
                            phoneError={driver2PhoneError}
                            onPhoneFocus={() => setDriver2PhoneError(null)}
                            onPhoneBlur={validateDriver2PhoneAsync}
                            containerClassName={`mb-4 ${containerClassName}`}
                            labelClassName={labelClassName}
                            inputContainerClassName={inputContainerClassName}
                        />
                        )}
                        <div className={`mb-4 ${containerClassName}`}>
                            <span className={labelClassName}>
                                {String(t('tms.task.drawer.vehicle'))}
                                {!isHandoverRoute && <span className="text-red-500 ml-1">*</span>}
                            </span>
                            <div className={inputContainerClassName}>
                                <AutoComplete
                                    className="w-full"
                                    placeholder={String(t(isHandoverRoute ? 'tms.task.drawer.inputLicensePlate' : 'tms.task.drawer.selectVehicle'))}
                                    options={vehicleOptions}
                                    value={filters.licensePlate}
                                    onChange={(value) => {
                                        const matchedVehicleOption = vehicleOptions.find(opt => opt.label === value);
                                        if (value && !matchedVehicleOption) {
                                            // 输入的车辆不在列表中（自由输入）
                                            setFilters(prev => ({ ...prev, licensePlate: value, vehicleId: '' }));
                                            setIsVehicleFreeInput(true);
                                        } else if (!value) {
                                            // 清空了输入
                                            setFilters(prev => ({ ...prev, licensePlate: '', vehicleId: '', vehicleModel: '', vehicleTypeId: null }));
                                            setIsVehicleFreeInput(false);
                                        } else {
                                            // 输入内容与下拉列表中的某一项完全匹配时，自动选择该车辆
                                            const valueAsString = String(matchedVehicleOption!.value);
                                            const matchedVehicle = vehiclesData?.getTmsVehiclesWithModel?.find(
                                                v => v.id.toString() === valueAsString
                                            );
                                            setFilters(prev => ({
                                                ...prev,
                                                vehicleId: valueAsString,
                                                licensePlate: matchedVehicleOption!.label as string,
                                                vehicleModel: matchedVehicle?.vehicleTypeName || prev.vehicleModel,
                                                vehicleTypeId: matchedVehicle?.vehicleTypeId || prev.vehicleTypeId,
                                            }));
                                            setIsVehicleFreeInput(false);
                                        }
                                    }}
                                    onSelect={(value, option) => {
                                        // 从列表中选择了车辆，自动填充对应的车型
                                        const matchedVehicle = vehiclesData?.getTmsVehiclesWithModel?.find(
                                            v => v.id.toString() === value.toString()
                                        );
                                        setFilters(prev => ({
                                            ...prev,
                                            vehicleId: value as string,
                                            licensePlate: option.label as string,
                                            vehicleModel: matchedVehicle?.vehicleTypeName || prev.vehicleModel,
                                            vehicleTypeId: matchedVehicle?.vehicleTypeId || prev.vehicleTypeId,
                                        }));
                                        setIsVehicleFreeInput(false);
                                    }}
                                    filterOption={(inputValue, option) =>
                                        (option?.label as string)?.toLowerCase().includes(inputValue.toLowerCase())
                                    }
                                />
                            </div>
                        </div>
                        <div className="mb-4">
                            <SingleSelect
                                label={String(t('tms.task.drawer.vehicleModel'))}
                                placeholder={String(t((isHandoverRoute || isVehicleFreeInput) ? 'tms.task.drawer.selectVehicleModel' : 'tms.task.drawer.autoFilledFromVehicle'))}
                                options={vehicleModelOptions}
                                value={filters.vehicleTypeId ? String(filters.vehicleTypeId) : undefined}
                                onChange={(value) => {
                                    const matched = vehicleModelOptions.find(o => String(o.value) === value);
                                    setFilters(prev => ({ ...prev, vehicleTypeId: value ? Number(value) : null, vehicleModel: matched?.label || '' }));
                                }}
                                spanClassName="w-[7em]"
                                forceHorizontal
                                disabled={!isHandoverRoute && !isVehicleFreeInput}
                                required={!isHandoverRoute}
                            />
                        </div>
                        <div className="mb-4">
                            <InputFilterItem
                                label={String(t('tms.task.drawer.trailerNumber'))}
                                placeholder={String(t('tms.task.drawer.placeholderTrailerNumber'))}
                                value={filters.trailerNumber}
                                onChange={(e) => handleFilterChange('trailerNumber', e.target.value)}
                                spanClassName="w-[7em]"
                                forceHorizontal
                                required={!isHandoverRoute}
                                maxLength={50}
                                showCount
                            />
                        </div>
                        <div className="mb-4">
                            <InputFilterItem
                                label={String(t('tms.task.drawer.gpsTracking'))}
                                value={filters.gpsTracking}
                                onChange={(e) => handleFilterChange('gpsTracking', e.target.value)}
                                spanClassName="w-[7em]"
                                placeholder={String(t('tms.task.drawer.placeholderGpsTracking'))}
                                forceHorizontal
                                maxLength={999}
                                showCount
                            />
                        </div>
                        <div className="text-black font-bold text-base mt-4 mb-4">
                            {String(t('tms.task.drawer.remarkInfo'))}
                        </div>
                        <div className="mb-4">
                            <InputFilterItem
                                label={String(t('tms.task.drawer.remarkLabel'))}
                                placeholder={String(t('tms.task.drawer.placeholderRemark'))}
                                value={filters.remark}
                                onChange={(e) => handleFilterChange('remark', e.target.value)}
                                spanClassName="w-[7em]"
                                forceHorizontal
                                maxLength={100}
                                showCount
                            />
                        </div>
                    </div>
                </div>
            </div>

            <TmsTaskConfirmDriverVehicleModal
                open={confirmModalVisible}
                onOk={handleConfirmSubmit}
                onCancel={() => setConfirmModalVisible(false)}
                title={getConfirmModalTitle()}
                confirmLoading={confirmSubmitting}
                isDriver1FreeInput={isDriver1FreeInput}
                isDriver2FreeInput={isDriver2FreeInput}
                isVehicleFreeInput={isVehicleFreeInput}
                driver1Form={driver1Form}
                driver2Form={driver2Form}
                vehicleForm={vehicleForm}
                carrierName={carrierName}
                driverVehicleOptions={driverVehicleOptions}
                driverVehicleLoading={driverVehicleLoading}
                vehicleModelOptions={vehicleModelOptions}
                fuelTypeOptions={fuelTypeOptions}
                statusOptions={statusOptions}
                onVehicleModelChange={handleVehicleTypeChange}
                zIndex={1100}
            />
        </EditableDrawer>
    );
};

export default CreateTemporaryTaskDrawer;


